import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { format, parseISO } from "date-fns";

import { PERMISSION_MAPPING } from "@/utils/constants";
import { Permission, UserType } from "@/types";
import { USER_TYPES } from "@/utils/constants";
import { AppDispatch, PropertyUnit } from "@/types/store";
import { PropertyType } from "@/types/enums";
import { PROTECTED_PATHS } from "@/router/protectedRoutes";
import { PropertyTotalValues } from "@/api/properties/types";
import { getShareLink } from "@/api/properties/thunks";
import { toast } from "@/hooks/useToast";
import {
  BackendConversation,
  BackendConversationsResponse,
} from "@/types/chat";
import {
  getConversationName,
  getConversationAvatar,
  getConversationParticipantId,
} from "@/utils/chatHelpers";
import { TapeChartProperty } from "@/types/tapechart";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Whether the user administers an organization. Accepts any UserType so
 * callers don't have to narrow before asking.
 */
export function isAdminUserType(userType: UserType): boolean {
  return (
    userType === USER_TYPES.ADMIN || userType === USER_TYPES.SUPER_ADMIN
  );
}

export function hasPermission(
  adminUserType: string,
  adminPermissions: Permission[],
  permission: (typeof PERMISSION_MAPPING)[keyof typeof PERMISSION_MAPPING]
): boolean {
  return (
    adminUserType === USER_TYPES.SUPER_ADMIN ||
    (adminUserType === USER_TYPES.ADMIN &&
      adminPermissions.some(
        (foundPermission) => foundPermission.name === permission
      ))
  );
}

export function setLocationTo(url: string) {
  window.location.href = url;
}

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
}

export function base64ToFile(base64: string, filename: string): File {
  const arr = base64.split(",");
  const mime = arr[0].match(/:(.*?);/)?.[1] || "";
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], filename, { type: mime });
}

export const handleMultiSelectChange = <T extends string>(
  fieldName: string,
  values: T[],
  setFieldValue: (
    field: string,
    value: string | T[],
    shouldValidate?: boolean
  ) => void
): void => {
  // Store as string if single value, otherwise as array
  const valueToStore = values.length === 1 ? values[0] : values;
  setFieldValue(fieldName, valueToStore);
};


export const convertUTCToLocalWithOffset = (utcDate: string): string => {
  const date = new Date(utcDate);
  const localDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
  return localDate.toISOString();
};

export const convertLocalToUTCWithOffset = (localDate: Date): Date => {
  // Format date to ISO date string and parse back using parseISO for consistent handling
  const dateString = format(localDate, "yyyy-MM-dd");
  return parseISO(dateString);
};

export const getListingPage = (propertyType: PropertyType) => {
  if (propertyType === PropertyType.SINGLE_FAMILY_HOME) {
    return PROTECTED_PATHS.CREATE_SINGLE_FAMILY_PROPERTY;
  } else {
    return PROTECTED_PATHS.CREATE_MULTI_FAMILY_PROPERTY;
  }
};

export const calculateTotalValues = (
  propertyUnits: PropertyUnit[]
): PropertyTotalValues => {
  return propertyUnits.reduce<PropertyTotalValues>(
    (acc, unit) => {
      return {
        size: acc.size + (unit.size || 0),
        totalBeds: acc.totalBeds + (unit.totalBeds || 0),
        parkingSpaces: acc.parkingSpaces + (unit.parkingSpaces || 0),
        bedrooms: acc.bedrooms + (unit.bedrooms || 0),
        fullBathrooms: acc.fullBathrooms + (unit.fullBathrooms || 0),
        halfBathrooms: acc.halfBathrooms + (unit.halfBathrooms || 0),
      };
    },
    {
      size: 0,
      totalBeds: 0,
      parkingSpaces: 0,
      bedrooms: 0,
      fullBathrooms: 0,
      halfBathrooms: 0,
    }
  );
};

export const handleShare = async (
  event: React.MouseEvent<HTMLSpanElement>,
  dispatch: AppDispatch,
  propertyId: string
) => {
  event.preventDefault();
  const response = await dispatch(getShareLink(propertyId));
  if (getShareLink.fulfilled.match(response)) {
    await navigator.clipboard.writeText(response.payload as string);
    toast({
      title: "Share link copied to clipboard",
      variant: "default",
    });
  } else {
    toast({
      title: "Unable to retrieve share link",
      variant: "destructive",
    });
  }
};

export const transformBackendConversations = (
  backendData: BackendConversationsResponse
) => {
  return backendData.conversations.map((conversation: BackendConversation) => ({
    id: conversation.id,
    name: getConversationName(conversation),
    lastMessage:
      conversation.lastMessage?.content ||
      conversation.subject ||
      "No messages yet",
    timestamp: conversation.lastMessageTimestamp,
    unreadMessagesCount: conversation.unreadMessagesCount,
    avatar: getConversationAvatar(conversation),
    userId: getConversationParticipantId(conversation),
  }));
};

// Format date as MM/DD/YYYY with leading zeros
export const formatDateWithLeadingZeros = (date: Date = new Date()): string => {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

export const formatDateTwoDigits = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
  });
};

export const mergeChipSelectItems = <T extends { id: string }>(
  existing: T[],
  incoming: T[]
): T[] => [
  ...existing,
  ...incoming.flatMap((h) => (existing.some((f) => f.id === h.id) ? [] : h)),
];

export const formatAddress = (property: TapeChartProperty | null) => {
  if (!property) return "Address not available";

  const parts = [];
  if (property.streetAddress) parts.push(property.streetAddress);
  if (property.city) parts.push(property.city);
  if (property.state) parts.push(property.state);
  if (property.zipCode) parts.push(property.zipCode);

  return parts.length > 0 ? parts.join(", ") : "Address not available";
};
